import { Component, input, OnChanges, output, signal, SimpleChanges } from '@angular/core';
import { Client, Section, _ } from '@mira-avant/mtlibs';
import { SectionService } from '../../../../services/section.service';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';

interface ClientSection {
	client: Client;
	sections: Section[];
}

@Component({
	selector: 'app-sections-selector',
	standalone: true,
	imports: [AngularModule, ZorroModule],
	templateUrl: './sections-selector.component.html',
	styleUrl: './sections-selector.component.scss',
})
export class SectionsSelectorComponent implements OnChanges {
	readonly selectedSectionUuids = input<string[]>([]);
	readonly selectedClients = input<Client[]>([]);
	readonly onChange = output<Section[]>();

	public allClientSections = signal<ClientSection[]>([]);
	public loading = signal(false);
	public selectedSectionIds = signal<string[]>([]);

	private debouncedUpdateSections: () => void = _.debounce(() => {
		this.updateSections();
	}, 1000);

	constructor(private sectionService: SectionService) {
		console.log('TODO: update me, need to implement search as there can be thousands of sections.');
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.selectedSectionIds.set(this.selectedSectionUuids());
		const selectedClients = this.selectedClients();
		if (!selectedClients.length) {
			this.allClientSections.set([]);
			return;
		}

		const didClientsChange =
			changes['selectedClients'] &&
			JSON.stringify(changes['selectedClients'].previousValue?.map((c: Client) => c.uuid).sort()) !==
				JSON.stringify(changes['selectedClients'].currentValue?.map((c: Client) => c.uuid).sort());

		if (!didClientsChange) {
			console.info('Clients did not change, skipping section reload');
			return;
		}

		this.debouncedUpdateSections();
	}

	private async updateSections(): Promise<void> {
		if (this.loading()) {
			return;
		}

		this.loading.set(true);
		const selectedClients = this.selectedClients();

		try {
			const newClientSections: ClientSection[] = [];

			for (const client of selectedClients) {
				const sections = await this.sectionService.getSectionsByClient$(client.uuid, 0, 100);

				newClientSections.push({ client, sections });
			}

			this.allClientSections.set(newClientSections);
		} catch (error) {
			console.error('Error loading sections:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public onSelectionChange(newSelectedSectionIds: string[]): void {
		this.selectedSectionIds.set(newSelectedSectionIds);
		const selectedSections = this.allClientSections().flatMap(clientSection =>
			clientSection.sections.filter(section => newSelectedSectionIds.includes(section.uuid))
		);
		this.onChange.emit(selectedSections);
	}
}
